import TagManager from 'react-gtm-module';
import { EcommerceEvent } from 'hooks/GTMAnalytics/types';
import { FEELIX_THEMES, SUBSCRIPTION_TYPE } from '../../constants';
import { EventType, RuleHouse } from './RuleHouse';
import { DataTarget, EventData } from './types';

const defaultRuleHouse = new RuleHouse();
defaultRuleHouse
  .withAnyFlow()
  .withThemes(FEELIX_THEMES.PARTNER)
  .withEventTypes(EventType.PARTNER)
  .withDataTargets(DataTarget.DEFAULT_DATA_LAYER)
  .allow();
defaultRuleHouse
  .withAnyFlow()
  .withThemes(FEELIX_THEMES.PARTNER)
  .withEventTypes(EventType.SEGMENT)
  .withDataTargets(DataTarget.SEGMENT_SERVER_SIDE, DataTarget.CHECKOUT_DATA_LAYER)
  .allow();
defaultRuleHouse
  .withFlows(SUBSCRIPTION_TYPE.QUOTE)
  .withThemes(FEELIX_THEMES.MODERN, FEELIX_THEMES.SOLO)
  .withEventTypes(EventType.QUOTE)
  .withDataTargets(DataTarget.DEFAULT_DATA_LAYER)
  .allow();
defaultRuleHouse
  .withFlows(SUBSCRIPTION_TYPE.SUBSCRIBE, SUBSCRIPTION_TYPE.TRIAL, SUBSCRIPTION_TYPE.NEW_TRIAL)
  .withThemes(FEELIX_THEMES.SOLO, FEELIX_THEMES.MODERN, FEELIX_THEMES.COMMON)
  .withEventTypes(EventType.REST, EventType.SEGMENT)
  .withAnyDataTarget()
  .allow();

defaultRuleHouse.validateOverlapping();

class EventManager {
  initialized: boolean;
  theme?: string;
  subscriptionType?: SUBSCRIPTION_TYPE;

  constructor() {
    this.initialized = false;
  }
  isEventAllowed = (layer: DataTarget, { event: eventName }: EventData) => {
    if (!this.initialized) {
      throw new Error('EventManager is not initialized');
    }
    return defaultRuleHouse.isAllowed(
      this.subscriptionType!,
      this.theme!,
      eventName,
      layer
    );
  };
  allow(layer: DataTarget, eventData: unknown): boolean {
    switch (layer) {
      case DataTarget.DEFAULT_DATA_LAYER:
      case DataTarget.NINETAILED:
      case DataTarget.CHECKOUT_DATA_LAYER:
      case DataTarget.SEGMENT_SERVER_SIDE:
        return this.isEventAllowed(layer, eventData as EventData);
    }
  }
}

const eventManager = new EventManager();

/**
 * @returns true if the event was pushed to the dataLayer, false otherwise
 */
export const pushToDataLayer = (eventData: unknown): boolean => {
  const windowRef: any = window;

  if (
    !windowRef.dataLayer ||
    !windowRef.dataLayer.push ||
    typeof windowRef.dataLayer.push !== 'function'
  ) {
    return false;
  }
  if (eventManager.allow(DataTarget.DEFAULT_DATA_LAYER, eventData)) {
    windowRef.dataLayer.push(eventData);
    return true;
  }
  return false;
};

/**
 * @returns true if the event was pushed to the CheckoutDataLayer, false otherwise
 */
export const pushToCheckoutDatalayer = (eventData: EcommerceEvent): boolean => {
  if (eventManager.allow(DataTarget.CHECKOUT_DATA_LAYER, eventData)) {
    TagManager.dataLayer({
      dataLayer: {
        ...eventData
      },
      dataLayerName: 'CheckoutDataLayer'
    });

    return true;
  }

  return false;
};

export const useNinetailedTrackEnabled = (eventName: string) => {
  return eventManager.allow(DataTarget.NINETAILED, { event: eventName });
}

export const useSegmentServerSideTrackEnabled = (eventName: string) => {
  return eventManager.allow(DataTarget.SEGMENT_SERVER_SIDE, { event: eventName });
}

type EventManagerProps = {
  theme: string;
  subscriptionType: SUBSCRIPTION_TYPE;
};

export const initialise = ({ theme, subscriptionType }: EventManagerProps) => {
  if (eventManager.initialized) {
    return;
  }

  eventManager.initialized = true;
  eventManager.theme = theme;
  eventManager.subscriptionType = subscriptionType;
};

/**
 * For testing purposes only
 */
export const reset = () => {
  eventManager.initialized = false;
  eventManager.theme = undefined;
  eventManager.subscriptionType = undefined;
}
